// AIDEV-NOTE: Hooks TanStack Query para fotos de produtos.
// useProdutoFotos: lista fotos de um produto (admin/detail)
// useProdutosFotosBatch: busca thumbnails em batch (catalogo, evita N+1)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produtosFotosService } from '@/services/produtos-fotos.service';
import type { ProdutoFoto, BatchFotosResponse } from '@azoupvendas/shared';

/**
 * Hook para listar fotos de um produto especifico
 */
export function useProdutoFotos(produtoCodigo: number | null) {
  return useQuery<ProdutoFoto[]>({
    queryKey: ['produto-fotos', produtoCodigo],
    enabled: produtoCodigo != null && produtoCodigo > 0,
    staleTime: 60_000,
    queryFn: () => produtosFotosService.listarFotos(produtoCodigo!)
  });
}

/**
 * Hook para buscar fotos em batch — usado no catalogo para evitar N+1
 * AIDEV-PERF: Uma unica request para todos os codigos da pagina
 */
export function useProdutosFotosBatch(codigos: number[]) {
  return useQuery<BatchFotosResponse>({
    queryKey: ['produto-fotos-batch', codigos.sort().join(',')],
    enabled: codigos.length > 0,
    staleTime: 60_000,
    queryFn: () => produtosFotosService.listarFotosBatch(codigos)
  });
}

/**
 * Hook para upload de foto (mutation)
 */
export function useUploadFoto(produtoCodigo: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => produtosFotosService.uploadFoto(produtoCodigo, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produto-fotos', produtoCodigo] });
      // Invalida tambem o batch cache
      queryClient.invalidateQueries({ queryKey: ['produto-fotos-batch'] });
    }
  });
}

/**
 * Hook para deletar foto (mutation)
 */
export function useDeleteFoto(produtoCodigo: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fotoId: string) => produtosFotosService.deletarFoto(fotoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produto-fotos', produtoCodigo] });
      queryClient.invalidateQueries({ queryKey: ['produto-fotos-batch'] });
    }
  });
}

/**
 * Hook para reordenar fotos (mutation)
 */
export function useReordenarFotos(produtoCodigo: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ordens: Array<{ id: string; ordem: number }>) =>
      produtosFotosService.reordenarFotos(produtoCodigo, ordens),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produto-fotos', produtoCodigo] });
    }
  });
}
